import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InternalAttachmentPolicy, InternalAttachmentAttempt, InternalAttachmentBatch } from '../../models/internal-attachment.model';
import {
  EstadoConexionUsuario,
  InternalChatContact,
  InternalChatConversation,
  InternalChatMessage,
  InternalChatMessagePage,
  InternalMessageReceipt,
  InternalMessageMention,
  InternalMessageStatus,
  InternalUserPresence,
  SendInternalChatMessageRequest,
  SendInternalConversationMessageRequest,
  UpdateInternalPresenceStatusRequest,
  UpdateInternalPresenceStatusManualRequest,
} from '../../models/internal-chat.model';

@Injectable({
  providedIn: 'root',
})
export class InternalChatService {
  private readonly apiUrl = `${environment.apiUrl}/internal-chat`;

  constructor(private http: HttpClient) {}

  getAttachmentPolicy(): Observable<InternalAttachmentPolicy> {
    return this.http.get<InternalAttachmentPolicy>(`${this.apiUrl}/attachment-policy`);
  }

  sendAttachments(conversation: InternalChatConversation, files: File[], attempts: InternalAttachmentAttempt[], replyToMessageId?: string | null): Observable<InternalAttachmentBatch> {
    const data = new FormData();
    files.forEach(file => data.append('files', file));
    data.append('metadata', JSON.stringify(attempts));
    if (replyToMessageId) data.append('replyToMessageId', replyToMessageId);
    if (conversation.conversationType === 'INDIVIDUAL' && conversation.contact) {
      return this.http.post<InternalAttachmentBatch>(`${this.apiUrl}/messages/attachments`, data, { params: { receiverUserId: conversation.contact.userId } });
    }
    return this.http.post<InternalAttachmentBatch>(`${this.apiUrl}/conversations/${conversation.conversationId}/messages/attachments`, data);
  }

  getContacts(): Observable<InternalChatContact[]> {
    return this.http.get<InternalChatContact[]>(`${this.apiUrl}/contacts`);
  }

  getConversations(): Observable<InternalChatConversation[]> {
    return this.http.get<InternalChatConversation[]>(
      `${this.apiUrl}/conversations`,
    );
  }

  getGroups(): Observable<InternalChatConversation[]> {
    return this.http.get<InternalChatConversation[]>(`${this.apiUrl}/groups`);
  }

  getPresenceStatuses(): Observable<EstadoConexionUsuario[]> {
    return this.http.get<EstadoConexionUsuario[]>(
      `${this.apiUrl}/presence-statuses`,
    );
  }

  updatePresenceStatus(
    request: UpdateInternalPresenceStatusRequest,
  ): Observable<InternalUserPresence> {
    return this.http.post<InternalUserPresence>(
      `${this.apiUrl}/presence/status`,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  updatePresenceStatusManual(
    request: UpdateInternalPresenceStatusManualRequest,
  ): Observable<InternalUserPresence> {
    return this.http.post<InternalUserPresence>(
      `${this.apiUrl}/presence/status/manual`,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  disconnectPresence(): Observable<InternalUserPresence> {
    return this.http.post<InternalUserPresence>(
      `${this.apiUrl}/presence/disconnect`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  getConversationMessagesPage(
    conversationId: number,
    page = 0,
    size = 20,
  ): Observable<InternalChatMessagePage> {
    return this.http.get<InternalChatMessagePage>(
      `${this.apiUrl}/conversations/${conversationId}/messages/page`,
      {
        params: {
          page,
          size,
        },
      },
    );
  }

  sendConversationMessage(
    conversationId: number,
    request: SendInternalConversationMessageRequest,
  ): Observable<InternalChatMessage> {
    return this.http.post<InternalChatMessage>(
      `${this.apiUrl}/conversations/${conversationId}/messages`,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  sendMessage(
    request: SendInternalChatMessageRequest,
  ): Observable<InternalChatMessage> {
    return this.http.post<InternalChatMessage>(
      `${this.apiUrl}/messages`,
      request,
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  sendMedia(receiverUserId: number, file: File, caption?: string, replyToMessageId?: string | null): Observable<InternalChatMessage> {
    const data = new FormData(); data.append('file', file);
    if (caption) { data.append('caption', caption); }
    if (replyToMessageId) { data.append('replyToMessageId', replyToMessageId); }
    return this.http.post<InternalChatMessage>(`${this.apiUrl}/messages/media`, data, { params: { receiverUserId } });
  }

  sendConversationMedia(conversationId: number, file: File, caption?: string, replyToMessageId?: string | null, mentions?: InternalMessageMention[]): Observable<InternalChatMessage> {
    const data = new FormData(); data.append('file', file);
    if (caption) { data.append('caption', caption); }
    if (replyToMessageId) { data.append('replyToMessageId', replyToMessageId); }
    if (mentions?.length) { data.append('mentions', JSON.stringify(mentions)); }
    return this.http.post<InternalChatMessage>(`${this.apiUrl}/conversations/${conversationId}/messages/media`, data);
  }

  markMessageAsRead(messageId: string): Observable<InternalMessageStatus> {
    return this.http.post<InternalMessageStatus>(
      `${this.apiUrl}/messages/${messageId}/read`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  markConversationByIdAsRead(
    conversationId: number,
  ): Observable<InternalMessageStatus[]> {
    return this.http.post<InternalMessageStatus[]>(
      `${this.apiUrl}/conversations/${conversationId}/read`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      },
    );
  }

  getMessageReceipts(messageId: string): Observable<InternalMessageReceipt[]> {
    return this.http.get<InternalMessageReceipt[]>(
      `${this.apiUrl}/messages/${messageId}/receipts`,
    );
  }
}
